import * as React from 'react';
import { Styler, StylerBaseProps } from '../Styler';
import { AccordionBorder, useComponentDefaultProps } from '../../theming';
import { AccordionContext } from './Accordion.context';
import { SvgIconProps } from '../SvgIcon/SvgIcon';
import { ChevronDownIcon } from '../../icons/ChevronDownIcon';
import { useStyles } from './Accordion.styles';
import { useId } from '../../hooks';

export interface AccordionProps extends StylerBaseProps {
    /**
     * The child components.
     */
    children: React.ReactNode;

    /**
     * If `true`, the uncontrolled accordion will be expanded by default.
     * @default false
     */
    defaultExpanded?: boolean;

    /**
     * If `true`, the accordion will be expanded. Use this prop when you want the accordion to be controlled.
     * @default undefined
     */
    expanded?: boolean;

    /**
     * Callback fired when the expanded state changes.
     * @param {React.SyntheticEvent} event The event source of the callback.
     * @param {boolean} expanded The current `expanded` state.
     */
    onChange?: (event: React.SyntheticEvent, expanded: boolean) => void;

    /**
     * An svg icon component. The icon which will be displayed at the end of the header to indicate that it is expandable.
     * <ChevronDownIcon />
     */
    chevron?: React.ReactElement<SvgIconProps>;

    /**
     * The unique id of the accordion. Used for accessibility markup.
     */
    id?: string;

    /**
     * Controls whether the accordion will have a top or bottom border.
     * @default bottom
     */
    border?: AccordionBorder;
}

const defaultProps: Partial<AccordionProps> = {
    chevron: <ChevronDownIcon />,
    border: 'bottom',
};

/**
 * The Accordion component is a component that allows you to create an accordion interface
 * that enables users to expand and collapse content sections. It is designed to be flexible and customizable, with
 * support for controlled and uncontrolled behavior, as well as theming and styling via the Styler
 * component props.
 */
export function Accordion(props: Readonly<AccordionProps>): React.JSX.Element {
    const {
        children,
        defaultExpanded = false,
        expanded: expandedProp,
        onChange,
        transitionDuration,
        chevron,
        border = 'bottom',
        id: idProp,
        ref,
        ...other
    } = useComponentDefaultProps('Accordion', defaultProps, props);

    const id = useId(idProp);

    const isControlled = expandedProp !== undefined;
    const [expandedState, setExpandedState] = React.useState(defaultExpanded);

    const { styles } = useStyles({ border });

    const isExpanded = React.useCallback(() => {
        return isControlled ? expandedProp : expandedState;
    }, [isControlled, expandedProp, expandedState]);

    const handleChange = React.useCallback(
        (event: React.SyntheticEvent<Element, Event>) => {
            if (!isControlled) {
                setExpandedState(!expandedState);
            }
            onChange?.(event, !isExpanded());
        },
        [expandedState, onChange, isExpanded, isControlled]
    );

    const contextValues = React.useMemo(
        () => ({
            headerId: `${id}-header`,
            contentId: `${id}-content`,
            expanded: isExpanded(),
            onToggle: handleChange,
            chevron,
        }),
        [handleChange, id, isExpanded, chevron]
    );

    return (
        <AccordionContext.Provider value={contextValues}>
            <Styler ref={ref} baseStyles={styles.root} data-accordion={true} {...other}>
                {children}
            </Styler>
        </AccordionContext.Provider>
    );
}
